import React, { useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import {
  useVendorById,
  useUploadVendorProfileImage,
  useVendorCategories,
  useMyVendor,
} from "../../hooks/useVendor";
import { useUser } from "../../hooks/useUser";
import SafeImage from "../../components/common/SafeImage";

export default function Details() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  // Fetch vendor details using useVendorById hook
  let vendorResponse, isLoading, isError, error;
  const { data: user } = useUser();
  if (user && user.role === "vendor" && String(id) === String(user.user_id)) {
    const myVendor = useMyVendor();
    vendorResponse = myVendor.data;
    isLoading = myVendor.isLoading;
    isError = myVendor.isError;
    error = myVendor.error;
  } else {
    const vendorById = useVendorById(id);
    vendorResponse = vendorById.data;
    isLoading = vendorById.isLoading;
    isError = vendorById.isError;
    error = vendorById.error;
  }
  const vendor = vendorResponse?.data;

  // Fetch categories for mapping vendor_type to name
  const { data: categoriesResponse, isLoading: categoriesLoading } =
    useVendorCategories();
  const categories = categoriesResponse?.data || [];

  // Find the category name for this vendor
  const vendorTypeName =
    categories.find(
      (cat) => String(cat.category_id) === String(vendor?.vendor_type),
    )?.category_name ||
    vendor?.vendor_type ||
    "N/A";

  // Profile image upload mutation using hook
  const { mutateAsync: uploadImage } = useUploadVendorProfileImage({
    isVendor: false, // Admin/employee updating vendor profile image
    onSuccess: (result) => {
      toast.success("Profile image updated!");
      // Refetch vendor details after upload to avoid cache mutation errors
      queryClient.invalidateQueries(["vendor", id]);
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update profile image");
    },
  });

  const handleProfileImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadImage({ id, file });
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statusClass = status === "active" ? "bg-success" : "bg-danger";
    return (
      <span className={`badge ${statusClass} text-capitalize`}>{status}</span>
    );
  };

  const getProfileImageUrl = (profileImage) => {
    if (!profileImage) return "/avatar-placeholder.png";
    if (profileImage.startsWith("/storage/")) {
      return (
        (import.meta.env.VITE_API_URL || "http://localhost:8000") + profileImage
      );
    }
    return profileImage;
  };

  if (isLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
        <div
          className="spinner-border text-primary mb-3"
          style={{ width: "3rem", height: "3rem" }}
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="text-muted">Loading vendor details...</p>
      </div>
    );
  }

  if (isError || !vendor) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100">
        <div className="alert alert-danger p-4 rounded-4 shadow-sm text-center">
          <h4 className="mb-2">
            <i className="ri-error-warning-line me-2"></i>Vendor Not Found
          </h4>
          <p>{error?.message || "The requested vendor could not be found."}</p>
          <button
            className="btn btn-primary mt-3"
            onClick={() => navigate("/vendor")}
          >
            <i className="ri-arrow-left-line me-2"></i>Back to Vendor List
          </button>
        </div>
      </div>
    );
  }

  // Address: try to use vendor.address (string or object)
  let address = vendor.address;
  if (address && typeof address === "object") {
    address = (
      <>
        {address.address_line1}
        <br />
        {address.address_line2 && (
          <>
            {address.address_line2}
            <br />
          </>
        )}
        {address.city}, {address.state} {address.postal_code}
        <br />
        {address.country}
      </>
    );
  }

  return (
    <div className="container py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12 d-flex flex-wrap justify-content-between align-items-center border-bottom pb-3 mb-3 gap-2">
          <div className="d-flex align-items-center gap-2">
            <i className="ri-store-2-line text-primary fs-2"></i>
            <h2 className="fw-bold mb-0">Vendor Details</h2>
          </div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/vendor">Vendors</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {vendor.user?.name || "Vendor Details"}
              </li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="row g-4">
        {/* Profile Card */}
        <div className="col-12 col-lg-4">
          <div className="card rounded-4 shadow-sm border-0">
            <div className="card-body d-flex flex-column align-items-center text-center">
              <div
                className="position-relative mb-3"
                style={{ width: 140, height: 140 }}
              >
                <SafeImage
                  src={getProfileImageUrl(vendor.user?.profile_image)}
                  className="img-fluid rounded-circle border border-3 border-white shadow"
                  alt={vendor.user?.name}
                  style={{ width: 140, height: 140, objectFit: "cover" }}
                  fallbackSrc="/avatar-placeholder.png"
                />
                {/* Pencil Icon Button for Profile Image Edit */}
                <button
                  type="button"
                  className="btn-light position-absolute d-flex align-items-center justify-content-center border shadow p-0"
                  style={{
                    bottom: 6,
                    right: 6,
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    lineHeight: 1,
                  }}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  title={uploading ? "Uploading..." : "Edit Profile Image"}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  disabled={uploading}
                >
                  {uploading ? (
                    <span
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : (
                    <i className="ri-pencil-line fs-5 text-secondary"></i>
                  )}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*"
                  onChange={handleProfileImageChange}
                  disabled={uploading}
                />
              </div>
              <h3 className="fw-semibold mb-1">{vendor.user?.name}</h3>
              <div className="text-muted mb-2">Vendor</div>
              <div className="mb-2 small text-muted">
                <span className="me-2">
                  <i className="ri-hashtag"></i> {vendor.vendor_id}
                </span>
                <span>
                  <i className="ri-calendar-line"></i> Joined{" "}
                  {formatDate(vendor.created_at)}
                </span>
              </div>
              <div className="d-flex gap-2 mt-2">
                <Link
                  to={`/vendoraddorupdate/${vendor.vendor_id}`}
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="ri-pencil-line me-1"></i>Edit
                </Link>
                <Link to="/vendor" className="btn btn-outline-secondary btn-sm">
                  <i className="ri-arrow-left-line me-1"></i>Back
                </Link>
              </div>
              <div className="d-flex gap-2 mt-2">
                <Link
                  to={`/product?vendor_id=${vendor.vendor_id}`}
                  className="btn btn-outline-success btn-sm"
                >
                  <i className="ri-store-2-line me-1"></i>Show Vendor Products
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Details Section */}
        <div className="col-12 col-lg-8">
          <div className="card rounded-4 shadow-sm border-0">
            <div className="card-body">
              <div className="row g-4">
                {/* Personal Info */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Full Name</div>
                    <div className="fw-semibold">
                      {vendor.user?.name || "N/A"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Email</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <a
                        href={`mailto:${vendor.user?.email}`}
                        className="text-decoration-none"
                      >
                        {vendor.user?.email || "N/A"}
                      </a>
                      <button
                        className="btn btn-link btn-sm p-0 text-primary"
                        title="Copy Email"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        onClick={() => {
                          navigator.clipboard.writeText(vendor.user?.email);
                          toast.success("Email copied!");
                        }}
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Phone</div>
                    <div className="fw-semibold">
                      {vendor.user?.phone_no || "N/A"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Address</div>
                    <div className="fw-semibold">{address || "N/A"}</div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Vendor Type</div>
                    <div className="fw-semibold">{vendorTypeName}</div>
                  </div>
                </div>
                {/* Account Info */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <div className="text-muted small mb-1">User ID</div>
                    <div className="fw-semibold">
                      {vendor.user?.user_id || "N/A"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Account Status</div>
                    <div className="fw-semibold">
                      {getStatusBadge(vendor.user?.status)}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Last Updated</div>
                    <div className="fw-semibold">
                      {formatDate(vendor.user?.updated_at) || "N/A"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Account Created</div>
                    <div className="fw-semibold">
                      {formatDate(vendor.user?.created_at) || "N/A"}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="text-muted small mb-1">Vendor ID</div>
                    <div className="fw-semibold d-flex align-items-center gap-2">
                      <span>#{vendor.vendor_id}</span>
                      <button
                        className="btn btn-link btn-sm p-0 text-primary"
                        title="Copy Vendor ID"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        onClick={() => {
                          navigator.clipboard.writeText(vendor.vendor_id);
                          toast.success("Vendor ID copied!");
                        }}
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
